import {makeFileReference, makeFolderReference} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {CopilotChatRepo} from '@github-ui/copilot-chat-types'
import {MultiFilePicker} from '@github-ui/copilot-spaces/components/MultiFilePicker'
import type {GitHubFileFormData} from '@github-ui/copilot-spaces/types'
import {fetchRepo} from '@github-ui/copilot-spaces/utils/fetch-repo'
import {type RefObject, useCallback} from 'react'

import {useChatManager} from '../utils/CopilotChatManagerContext'

interface CopilotFilePickerProps {
  isOpen: boolean
  onClose: () => void
  returnFocusRef: RefObject<HTMLButtonElement | null>
  initialRepo?: CopilotChatRepo | null
  onRepoSelect?: (repo: CopilotChatRepo | null) => void
}

export function CopilotFilePicker({
  isOpen,
  onClose,
  returnFocusRef,
  initialRepo,
  onRepoSelect,
}: CopilotFilePickerProps) {
  const manager = useChatManager()

  const handleSaveFiles = useCallback(
    async (formData: GitHubFileFormData[]) => {
      if (formData.length === 0) {
        onClose()
        return
      }

      // since the files can only come from one repo at a time, we only need to fetch one repository
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const repo = await fetchRepo(formData[0]!.repositoryId)

      const references = formData.map(item => {
        if (item.type === 'github_folder') {
          return makeFolderReference(item.filePath, repo)
        } else {
          return makeFileReference(item.filePath, repo)
        }
      })

      for (const reference of references) {
        manager.addReference(reference, 'references-menu')
      }

      onClose()
    },
    [manager, onClose],
  )

  if (!isOpen) {
    return null
  }

  return (
    <MultiFilePicker
      formData={[]}
      onCancel={onClose}
      onSaveFiles={handleSaveFiles}
      attachmentButtonRef={returnFocusRef}
      initialRepo={initialRepo}
      onRepoSelect={onRepoSelect}
      allowFolderReferences
      maxFileCount={20}
    />
  )
}
